/**
 * ReleaseSectionsRow
 *
 * One draggable dashboard row of release sections, for one role:
 * - reviewer: "In review" for dual-role users, or "Waiting for your release" for
 *   reviewer-only users (items whose requested changes were made pinned first),
 *   plus "Released by you"
 * - operator: "In review" for creator-only users, then "Changes requested" and
 *   "Ready to schedule" (pipeline order)
 * Empty sections are hidden; a group with nothing in it shows one "All caught up" line.
 * Data comes from the server's `release-sections` response (one query, see useReleaseSections).
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardSection } from '@/components/dashboard/DashboardSection';
import { SectionTitle } from '@/components/dashboard/SectionTitle';
import { HorizontalCardCarousel } from '@/components/dashboard/HorizontalCardCarousel';
import { ReleaseItemCard } from '@/components/dashboard/release/ReleaseItemCard';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { buildBoardReleaseHref } from '@/utils/releaseLinks';
import type {
  ReleaseSectionKey,
  ReleaseSectionPayload,
  ReleaseSectionsBySlot,
  SectionItem,
} from '@/types/releaseSections';

export type ReleaseRowGroup = 'reviewer' | 'operator';

interface SectionSpec {
  key: ReleaseSectionKey;
  title: string;
  icon: string;
  payload: ReleaseSectionPayload | undefined;
  /** Ids of items shown pinned (changes made) */
  pinnedIds?: ReadonlySet<string>;
}

interface ReleaseSectionsRowProps {
  group: ReleaseRowGroup;
  sections: ReleaseSectionsBySlot | undefined;
  isNew: (item: SectionItem) => boolean;
  /** Show the "Release" group heading above the sections */
  showHeading: boolean;
  /** Creator-only users have no reviewer row to host the merged In review section. */
  showInReview?: boolean;
}

export const ReleaseSectionsRow = ({ group, sections, isNew, showHeading, showInReview = false }: ReleaseSectionsRowProps) => {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState<Partial<Record<ReleaseSectionKey, boolean>>>({});

  const specs = useMemo<SectionSpec[]>(() => {
    if (group === 'reviewer') {
      const changesMade = sections?.changesMade;
      const pinned = changesMade?.items ?? [];
      const pinnedIds = new Set(pinned.map((item) => item.id));
      // One list: changes-made items first, then the rest; nothing appears twice.
      const awaiting = sections?.awaiting;
      const rest = (awaiting?.items ?? []).filter((item) => !pinnedIds.has(item.id));
      const items = [...pinned, ...rest];
      const hasAwaiting = awaiting !== undefined || changesMade !== undefined;
      const inReview = sections?.inReview;
      const reviewItems = inReview
        ? [...pinned, ...inReview.items.filter((item) => !pinnedIds.has(item.id))]
        : items;
      const reviewPayload = inReview
        ? { total: inReview.total, items: reviewItems }
        : hasAwaiting
          ? { total: awaiting?.total ?? changesMade?.total ?? 0, items }
          : undefined;
      return [
        {
          key: 'awaiting',
          title: inReview ? t('release_pipeline_in_review') : t('release_section_awaiting_title'),
          icon: ICONS.REVIEW,
          pinnedIds,
          // On dual-role accounts, use the full board stage. Reviewer-only accounts
          // still see their actionable awaiting list.
          payload: reviewPayload,
        },
        {
          key: 'released_by_me',
          title: t('release_section_released_by_me_title'),
          icon: ICONS.RELEASE_STAMP,
          payload: sections?.releasedByMe,
        },
      ];
    }
    return [
      ...(showInReview && sections?.inReview
        ? [
          {
            key: 'awaiting' as const,
            title: t('release_pipeline_in_review'),
            icon: ICONS.REVIEW,
            payload: sections.inReview,
          },
        ]
        : []),
      {
        key: 'changes_requested',
        title: t('release_section_changes_requested_title'),
        icon: ICONS.CAUTION,
        payload: sections?.changesRequested,
      },
      {
        key: 'ready_to_schedule',
        title: t('release_section_ready_title'),
        icon: ICONS.CALENDAR,
        payload: sections?.readyToSchedule,
      },
    ];
  }, [group, sections, showInReview, t]);

  // Visible by total, not by loaded items: a section whose items failed to load must not read as "all caught up"
  const visible = specs.filter((spec) => (spec.payload?.total ?? 0) > 0 || (spec.payload?.items.length ?? 0) > 0);
  const anyCollapsed = visible.some((spec) => collapsed[spec.key] ?? false);
  const seeAll = (): void => navigate(buildBoardReleaseHref());

  if (visible.length === 0) {
    const lead = specs[0];
    return (
      <div className="space-y-4">
        {showHeading && <SectionTitle title={t('release_group_title')} />}
        <DashboardSection id={`release-${group}`} title={lead?.title ?? ''} icon={lead?.icon}>
          <p className="flex items-center gap-2 text-sm text-gray-600">
            <Icon name={ICONS.CHECK_CIRCLE} size="sm" color="text-primary-500" ariaHidden />
            {t('release_section_all_caught_up')}
          </p>
        </DashboardSection>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showHeading && <SectionTitle title={t('release_group_title')} />}
      {/* Two per row from lg (like the other paired rows); an odd last section spans the full width */}
      <div
        className={`grid grid-cols-1 gap-6 lg:grid-cols-2 ${anyCollapsed ? 'items-start' : ''}`}
      >
      {visible.map((spec, index) => {
        const payload = spec.payload;
        if (!payload) return null;
        const isCollapsed = collapsed[spec.key] ?? false;
        return (
          <DashboardSection
            key={spec.key}
            id={`release-${spec.key}`}
            className={`min-w-0 ${index === visible.length - 1 && visible.length % 2 === 1 ? 'lg:col-span-2' : ''}`}
            title={spec.title}
            icon={spec.icon}
            collapsed={isCollapsed}
            onToggleCollapse={() =>
              setCollapsed((current) => ({ ...current, [spec.key]: !isCollapsed }))
            }
            headerActions={
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-gray-700">
                  {payload.total}
                </span>
                <button
                  type="button"
                  onClick={seeAll}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline"
                >
                  {t('release_section_see_all')}
                </button>
              </div>
            }
          >
            {payload.items.length === 0 ? (
              <button
                type="button"
                onClick={seeAll}
                className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline"
              >
                {t('release_section_open_board')}
              </button>
            ) : (
            <HorizontalCardCarousel gap="gap-4">
              {payload.items.map((item) => (
                <ReleaseItemCard
                  key={item.id}
                  item={item}
                  section={spec.key}
                  isNew={isNew(item)}
                  pinned={spec.pinnedIds?.has(item.id) ?? false}
                />
              ))}
            </HorizontalCardCarousel>
            )}
          </DashboardSection>
        );
      })}
      </div>
    </div>
  );
};

interface ReleaseSectionsErrorProps {
  onRetry: () => void;
}

/** Manual retry when the sections could not be loaded (no automatic retry loop). */
export const ReleaseSectionsError = ({ onRetry }: ReleaseSectionsErrorProps) => {
  const { t } = useTranslation('dashboard');
  return (
    <DashboardSection
      id="release-error"
      title={t('release_group_title')}
      icon={ICONS.ALARM_CLOCK}
    >
      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
        <span>{t('release_section_error')}</span>
        <button
          type="button"
          onClick={onRetry}
          className="rounded-md border border-gray-200 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
          {t('release_section_retry')}
        </button>
      </div>
    </DashboardSection>
  );
};
